import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, FormControl, Grid, InputLabel, ListItemText, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import { AccessTime, CheckCircle, Restaurant } from '@mui/icons-material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import Navbar from '../setup/Navbar';
import '../../services/GlobalVariables';

const formatDuration = value => {
    const seconds = Math.max(0, Number(value) || 0);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return [hours, minutes, secs].map(part => String(part).padStart(2, '0')).join(':');
};

const KOT_COLUMNS = [
    { key: 'dineIn', label: 'Dine In', color: '#1565c0' },
    { key: 'takeaway', label: 'Takeaway', color: '#6a1b9a' },
    { key: 'delivery', label: 'Delivery', color: '#ef6c00' },
    { key: 'foodpanda', label: 'Foodpanda', color: '#d70f64' }
];

const getOrderTypeColumn = orderType => {
    const type = String(orderType || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    if (type.includes('foodpanda')) return 'foodpanda';
    if (type.includes('delivery')) return 'delivery';
    if (type.includes('dine') || type.includes('table')) return 'dineIn';
    return 'takeaway';
};

const KITCHEN_BADGE_COLORS = ['#1565c0', '#7b1fa2', '#00897b', '#ef6c00', '#c62828', '#2e7d32', '#6d4c41', '#455a64'];
const getKitchenBadgeColor = kitchenId => {
    const text = String(kitchenId || '');
    const hash = Array.from(text).reduce((total, character) => total + character.charCodeAt(0), 0);
    return KITCHEN_BADGE_COLORS[hash % KITCHEN_BADGE_COLORS.length];
};

export default function KitchenOrderScreen() {
    const { user } = useSelector(state => state.auth || {});
    const [kitchens, setKitchens] = useState([]);
    const [kitchenIds, setKitchenIds] = useState([]);
    const [orders, setOrders] = useState({ pending: [], completed: [], stats: { totalOut: 0, onTimeOutCount: 0, lateOutCount: 0 } });
    const [kotNo, setKotNo] = useState('');
    const [markingOutKot, setMarkingOutKot] = useState(null);
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [now, setNow] = useState(Date.now());
    const inputRef = useRef(null);

    useEffect(() => {
        axios.get(`${global.SET_API_URL}kitchens/list`).then(response => {
            const active = (response.data?.kitchens || []).filter(kitchen => kitchen.status === 'active');
            setKitchens(active);
            if (active.length === 1) setKitchenIds([String(active[0].kitchenId)]);
        }).catch(error => setMessage({ type: 'error', text: error.response?.data?.message || 'Unable to load kitchens.' }));
    }, []);

    const loadOrders = useCallback(async (quiet = false) => {
        if (!kitchenIds.length) return;
        if (!quiet) setLoading(true);
        try {
            const responses = await Promise.all(kitchenIds.map(selectedKitchenId =>
                axios.get(`${global.SET_API_URL}kitchenOrderScreen/${selectedKitchenId}/orders`, { params: { userID: user?.userID } })
            ));
            const combined = responses.reduce((result, response, index) => {
                const selectedKitchenId = kitchenIds[index];
                const kitchen = kitchens.find(item => String(item.kitchenId) === selectedKitchenId);
                const attachKitchen = order => ({ ...order, kitchenId: order.kitchenId || selectedKitchenId, kitchenName: kitchen?.kitchenName || `Kitchen ${selectedKitchenId}` });
                result.pending.push(...(response.data?.pending || []).map(attachKitchen));
                result.completed.push(...(response.data?.completed || []).map(attachKitchen));
                result.stats.totalOut += Number(response.data?.stats?.totalOut) || 0;
                result.stats.onTimeOutCount += Number(response.data?.stats?.onTimeOutCount) || 0;
                result.stats.lateOutCount += Number(response.data?.stats?.lateOutCount) || 0;
                return result;
            }, { pending: [], completed: [], stats: { totalOut: 0, onTimeOutCount: 0, lateOutCount: 0 } });
            setOrders(combined);
        } catch (error) {
            if (!quiet) setMessage({ type: 'error', text: error.response?.data?.message || 'Unable to load orders.' });
        } finally { if (!quiet) setLoading(false); }
    }, [kitchenIds, kitchens, user?.userID]);

    useEffect(() => {
        setOrders({ pending: [], completed: [], stats: { totalOut: 0, onTimeOutCount: 0, lateOutCount: 0 } });
        setMessage(null);
        loadOrders();
        if (kitchenIds.length) setTimeout(() => inputRef.current?.focus(), 100);
        const refresh = setInterval(() => loadOrders(true), 10000);
        return () => clearInterval(refresh);
    }, [kitchenIds, loadOrders]);

    useEffect(() => {
        const events = new EventSource(`${global.SET_API_URL}kitchenOrderScreen/events`);
        const refresh = () => loadOrders(true);
        events.addEventListener('kitchen-order-updated', refresh);
        return () => {
            events.removeEventListener('kitchen-order-updated', refresh);
            events.close();
        };
    }, [loadOrders]);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    const markKotOut = async (targetKotNo, targetKitchenId = null) => {
        if (!kitchenIds.length || !targetKotNo || markingOutKot) return;
        let resolvedKitchenId = targetKitchenId;
        if (!resolvedKitchenId) {
            const matches = orders.pending.filter(order => Number(order.kotNo) === Number(targetKotNo));
            if (matches.length !== 1) {
                setMessage({ type: 'warning', text: matches.length > 1 ? 'This KOT is present in multiple kitchens. Please use the OUT button on the required kitchen card.' : 'KOT not found in the selected kitchens.' });
                return;
            }
            resolvedKitchenId = matches[0].kitchenId;
        }
        const markingKey = `${resolvedKitchenId}:${targetKotNo}`;
        setMarkingOutKot(markingKey);
        try {
            const response = await axios.post(`${global.SET_API_URL}kitchenOrderScreen/${resolvedKitchenId}/out`, { kotNo: Number(targetKotNo), userID: user?.userID });
            setMessage({ type: 'success', text: response.data.message });
            setKotNo('');
            await loadOrders(true);
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Unable to mark the KOT Out.' });
            setKotNo('');
        } finally {
            setMarkingOutKot(null);
            inputRef.current?.focus();
        }
    };

    const submitKot = event => {
        event.preventDefault();
        markKotOut(kotNo);
    };

    const selectedKitchens = useMemo(() => kitchens.filter(k => kitchenIds.includes(String(k.kitchenId))), [kitchens, kitchenIds]);
    const pendingItemSummary = useMemo(() => {
        const summary = new Map();
        orders.pending.forEach(order => {
            String(order.itemSummary || '').split('||').filter(Boolean).forEach(line => {
                const match = line.match(/^([\d.]+)\s*x\s*(.+)$/i);
                const quantity = match ? Number(match[1]) || 0 : 1;
                const itemName = (match ? match[2] : line).trim();
                const key = itemName.toLowerCase();
                const current = summary.get(key) || { itemName, quantity: 0, kotNumbers: new Set() };
                current.quantity += quantity;
                current.kotNumbers.add(order.kotNo);
                summary.set(key, current);
            });
        });
        return Array.from(summary.values())
            .map(item => ({ ...item, kotCount: item.kotNumbers.size }))
            .sort((a, b) => b.quantity - a.quantity || a.itemName.localeCompare(b.itemName));
    }, [orders.pending]);
    const pendingOrdersByType = useMemo(() => {
        const grouped = Object.fromEntries(KOT_COLUMNS.map(column => [column.key, []]));
        orders.pending.forEach(order => grouped[getOrderTypeColumn(order.orderType)].push(order));
        Object.values(grouped).forEach(columnOrders => columnOrders.sort((a, b) => {
            const timeDifference = new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime();
            return timeDifference || Number(a.kotNo || 0) - Number(b.kotNo || 0);
        }));
        return grouped;
    }, [orders.pending]);
    const liveSeconds = order => Math.max(Number(order.elapsedSeconds) || 0, Math.floor((now - new Date(order.startedAt).getTime()) / 1000));

    return <>
        <Navbar />
        <Box sx={{ minHeight: '100vh', bgcolor: '#101820', color: 'white', p: { xs: 2, md: 3 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
                <Box><Typography variant="h4" fontWeight={800}>Kitchen Order Screen</Typography><Typography color="grey.400">Select a kitchen, enter the KOT number, and press Enter.</Typography></Box>
                <FormControl sx={{ minWidth: 280, bgcolor: 'white', borderRadius: 1 }}>
                    <InputLabel>Kitchen</InputLabel>
                    <Select multiple value={kitchenIds} label="Kitchens" onChange={event => setKitchenIds(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
                        renderValue={selected => kitchens.filter(kitchen => selected.includes(String(kitchen.kitchenId))).map(kitchen => kitchen.kitchenName).join(' + ')}>
                        {kitchens.map(kitchen => <MenuItem key={kitchen.kitchenId} value={String(kitchen.kitchenId)}><Checkbox checked={kitchenIds.includes(String(kitchen.kitchenId))} /><ListItemText primary={kitchen.kitchenName} /></MenuItem>)}
                    </Select>
                </FormControl>
            </Stack>

            {kitchenIds.length > 0 && <Paper component="form" onSubmit={submitKot} sx={{ p: 2, mb: 3, bgcolor: '#fff3cd', border: '3px solid #ffb300' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" spacing={2}>
                    <Restaurant color="warning" sx={{ fontSize: 45 }} />
                    <Box sx={{ flex: 1 }}><Typography variant="h6" color="text.primary" fontWeight={700}>{selectedKitchens.map(kitchen => kitchen.kitchenName).join(' + ')}</Typography><Typography color="text.secondary">Enter the KOT number using the physical numpad</Typography></Box>
                    <TextField inputRef={inputRef} autoFocus value={kotNo} onChange={event => setKotNo(event.target.value.replace(/\D/g, '').slice(0, 8))}
                        placeholder="KOT #" inputMode="numeric" sx={{ width: { xs: '100%', md: 260 }, bgcolor: 'white' }}
                        inputProps={{ style: { fontSize: 34, fontWeight: 800, textAlign: 'center' }, 'aria-label': 'KOT number' }} />
                    <Chip label="Press ENTER" color="success" sx={{ fontSize: 18, fontWeight: 800, height: 48, px: 1 }} />
                </Stack>
            </Paper>}

            {message && <Alert severity={message.type} onClose={() => setMessage(null)} sx={{ mb: 2, fontSize: 16 }}>{message.text}</Alert>}
            {!kitchenIds.length ? <Alert severity="info">Select one or more kitchens to view their orders.</Alert> : loading ? <Box textAlign="center" py={8}><CircularProgress /></Box> : <>
                <Stack direction="row" spacing={2} mb={2} flexWrap="wrap" useFlexGap>
                    <Paper sx={{ px: 2.5, py: 1.5, minWidth: 180 }}><Typography color="text.secondary">Pending Orders</Typography><Typography variant="h4" fontWeight={900}>{orders.pending.length}</Typography></Paper>
                    <Paper sx={{ px: 2.5, py: 1.5, minWidth: 180, bgcolor: '#e8f5e9', border: '2px solid #2e7d32' }}><Typography color="success.main" fontWeight={700}>On Time Out (&lt;20 min)</Typography><Typography variant="h4" color="success.main" fontWeight={900}>{Number(orders.stats?.onTimeOutCount) || 0}</Typography></Paper>
                    <Paper sx={{ px: 2.5, py: 1.5, minWidth: 180, bgcolor: '#ffebee', border: '2px solid #d32f2f' }}><Typography color="error.main" fontWeight={700}>Late Out (20+ min)</Typography><Typography variant="h4" color="error.main" fontWeight={900}>{Number(orders.stats?.lateOutCount) || 0}</Typography></Paper>
                    <Paper sx={{ px: 2.5, py: 1.5, minWidth: 180 }}><Typography color="text.secondary">Total Out</Typography><Typography variant="h4" fontWeight={900}>{Number(orders.stats?.totalOut) || 0}</Typography></Paper>
                </Stack>
                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} md={8}>
                        <Typography variant="h5" fontWeight={800} mb={2}>Pending Orders ({orders.pending.length})</Typography>
                        {!orders.pending.length && <Alert severity="success" sx={{ mb: 3 }}>There are no pending orders in this kitchen.</Alert>}
                        <Grid container spacing={1.5} mb={4}>
                            {KOT_COLUMNS.map(column => <Grid item xs={12} sm={6} lg={3} key={column.key}>
                                <Paper sx={{ bgcolor: '#18232d', minHeight: 180, overflow: 'hidden', border: '1px solid #33424f' }}>
                                    <Box sx={{ px: 1.5, py: 1.25, bgcolor: column.color, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography fontWeight={900}>{column.label}</Typography>
                                        <Chip label={pendingOrdersByType[column.key].length} size="small" sx={{ bgcolor: 'white', fontWeight: 900 }} />
                                    </Box>
                                    <Stack spacing={1.25} sx={{ p: 1.25 }}>
                                        {!pendingOrdersByType[column.key].length && <Typography color="grey.500" textAlign="center" py={3}>No KOT</Typography>}
                                        {pendingOrdersByType[column.key].map(order => {
                                            const seconds = liveSeconds(order), late = seconds >= 1200, warning = seconds >= 600;
                                            return <Card key={order.trackingID} sx={{ borderTop: '6px solid', borderColor: late ? 'error.main' : warning ? 'warning.main' : 'success.main' }}>
                                                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                    <Typography variant="h6" fontWeight={900}>KOT #{order.kotNo}</Typography>
                                                    <Chip
                                                        label={order.kitchenName}
                                                        size="small"
                                                        sx={{ bgcolor: getKitchenBadgeColor(order.kitchenId), color: 'white', fontWeight: 900, mt: 0.5 }}
                                                    />
                                                    <Chip icon={<AccessTime />} label={formatDuration(seconds)} color={late ? 'error' : warning ? 'warning' : 'success'} size="small" sx={{ my: 0.75 }} />
                                                    {order.tableLabel && <Typography color="text.secondary" mb={0.5}>Table {order.tableLabel}</Typography>}
                                                    <Stack spacing={0.25}>{String(order.itemSummary || '').split('||').filter(Boolean).map((item, index) => <Typography key={index} fontSize={14} fontWeight={600}>{item}</Typography>)}</Stack>
                                                    <Button type="button" fullWidth variant="contained" color="success"
                                                        disabled={markingOutKot === `${order.kitchenId}:${order.kotNo}`} onClick={() => markKotOut(order.kotNo, order.kitchenId)}
                                                        sx={{ mt: 1.25, fontWeight: 900 }}>
                                                        {markingOutKot === `${order.kitchenId}:${order.kotNo}` ? 'Marking...' : 'OUT'}
                                                    </Button>
                                                </CardContent>
                                            </Card>;
                                        })}
                                    </Stack>
                                </Paper>
                            </Grid>)}
                        </Grid>
                        <Typography variant="h6" fontWeight={700} color="grey.300" mb={1}>Recent Out</Typography>
                        <Grid container spacing={1}>{orders.completed.map(order => <Grid item xs={12} xl={6} key={order.trackingID}><Paper sx={{ p: 1.5 }}><Stack direction="row" justifyContent="space-between"><Typography fontWeight={800}><CheckCircle color="success" sx={{ verticalAlign: 'middle', mr: 1 }} />KOT #{order.kotNo}</Typography><Typography fontWeight={700}>{formatDuration(order.elapsedSeconds)}</Typography></Stack></Paper></Grid>)}</Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ bgcolor: '#f7f9fc', overflow: 'hidden', position: { md: 'sticky' }, top: { md: 16 } }}>
                            <Box sx={{ p: 2, bgcolor: '#8b0000', color: 'white' }}>
                                <Typography variant="h5" fontWeight={800}>Pending Items Summary</Typography>
                                <Typography>{pendingItemSummary.length} items • {orders.pending.length} KOTs</Typography>
                            </Box>
                            <Box sx={{ maxHeight: { md: 'calc(100vh - 330px)' }, overflowY: 'auto' }}>
                                {!pendingItemSummary.length ? <Box p={3}><Typography color="text.secondary">There are no pending items.</Typography></Box> : pendingItemSummary.map((item, index) =>
                                    <Stack key={item.itemName} direction="row" alignItems="center" spacing={2} sx={{ px: 2, py: 1.5, borderBottom: '1px solid #dde2e8', bgcolor: index % 2 ? 'white' : '#f7f9fc' }}>
                                        <Typography color="text.secondary" fontWeight={700} sx={{ width: 30 }}>{index + 1}</Typography>
                                        <Typography color="text.primary" fontWeight={800} sx={{ flex: 1, fontSize: 18 }}>{item.itemName}</Typography>
                                        <Chip label={`${item.kotCount} KOT${item.kotCount === 1 ? '' : 's'}`} variant="outlined" />
                                        <Box sx={{ minWidth: 70, textAlign: 'center', bgcolor: '#ffb300', color: '#151515', borderRadius: 1, px: 1.5, py: 0.75 }}><Typography variant="h5" fontWeight={900}>{item.quantity}</Typography></Box>
                                    </Stack>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </>}
        </Box>
    </>;
}
